/* ════════════════════════════════════════════════════════════════════
 *  MDL Studies — calculation, statistics, matrix builder, e-signature
 *  engine and report generation.  These are faithful ports of the design
 *  prototype's pure JavaScript (40 CFR 136 App. B Rev 2 maths), kept in a
 *  framework-free module so the React layer stays thin.  HTML-string
 *  builders (matrix / signatures / report) reproduce the prototype markup
 *  verbatim and are injected via dangerouslySetInnerHTML — guaranteeing
 *  the design renders exactly as in the source HTML.
 *
 *  token-exempt (Standard 1): the hex in the HTML-string builders below are
 *  REPORT-DOCUMENT colours (40 CFR 136 App. B matrix / statistics / masthead —
 *  reds for excluded-outlier & exceedance, greens for pass, blues for headers,
 *  greys for placeholders). This is a regulatory report reproduced verbatim, the
 *  same "report/PDF colours" exemption as the PDF report generators — kept exact
 *  so the compliance document's appearance never drifts. App-UI colours live in
 *  MdlStudiesPage.tsx / mdlStudies.css (fully tokenised to --ai-*).
 * ════════════════════════════════════════════════════════════════════ */

import { HOUSTON_LOGO_DATA_URI } from './houstonLogo';

/* ── Types ── */
export type ModelType = 'initial' | 'verif';
// 'pending' = Pending Submission (after Save, before the Submit e-signature, 2026-06-27).
// 'draft' is the internal name kept for the post-Submit "Submitted" state.
export type StudyStatus = 'pending' | 'draft' | 'validated' | 'approved' | 'released';

export interface Analyte {
  name: string; unit: string; spike: number; mdls: number; mdlb: number; final: number;
  current?: number; ratio?: number; status?: string;
  blankTotal?: number; blankExceed?: number;   // trailing-24-month method-blank exceedance (Verification §4 / gap 2.2)
}
export interface Kpi { lbl: string; val: string; unit?: string; cls?: string; color?: string; sub?: string; accent?: boolean; }
export interface Signature {
  action: string; meaning: string; reason?: string; name: string; userId: string;
  role?: string; mode: 'iso' | 'part11'; mfa?: boolean; when: Date | string;
  recordHash?: string; seeded?: boolean;
}
export interface Attachment { id?: string; name: string; size: number; type: string; when: Date | string; by: string; }
export interface MatrixRow { id: string; type: string; sample?: string; qcType?: string; vals: number[]; excluded?: boolean; }
/** Stable key for a replicate row (QC type | sample code | batch) — used for outlier exclusion (gap 3.4). */
export const replicateKey = (r: { qcType?: string; sample?: string; id?: string }) => `${r.qcType || ''}|${r.sample || ''}|${r.id || ''}`;
/** Base SysSampleCode with any trailing re-read / dilution suffix removed (…-001R1 → …-001),
 *  so a sample and its re-reads collapse to one matrix row / one replicate. */
export const baseSysCode = (s: string | null | undefined): string => (s || '').replace(/R\d+$/, '');
/** A spike level = the QC types that share the same per-analyte spike-amount profile,
 *  pooled together for one valid MDL-Spike SD (EPA 40 CFR 136 App. B). */
export interface SpikeLevel { label: string; qcTypes: string[]; amts: number[]; rows: MatrixRow[]; stats: MatrixStat[]; }
/** Procedure-compliance checks vs 40 CFR 136 App. B (gaps 3.1 / 3.2 / 3.3 / 4.2). */
export interface MdlCompliance {
  batches: number; dates: number; spreadOk: boolean;             // ≥3 batches & ≥3 calendar dates (3.1)
  nSpike: number; nBlank: number; minSpikeOk: boolean; minBlankOk: boolean;   // ≥7 spikes & ≥7 blanks (3.2)
  spikeLevel: { analyte: string; ratio: number | null; flag: 'ok' | 'low' | 'high' | 'na' }[];   // 2–10× MDL (3.3)
  spikeLevelOk: boolean;
  cadence: { quarter: string; spikes: number; ok: boolean }[];   // ≥2 MDL spikes per calendar quarter (Verification §3 / 4.2)
  cadenceOk: boolean;
}
export interface MatrixStat {
  avgSpk: number; nSpk: number; stdSpk: number; t: number; mdlSpk: number;
  avgBlk: number; nBlk: number; stdBlk: number; mdlBlk: number;
  final: number; current: number | null; ratio: number | null; verif: string | null;
  // Verification §4 second criterion (gap 2.2): trailing-24-month method-blank exceedance.
  blankTotal?: number; blankExceed?: number; blankExceedOk?: boolean | null;
}

/** Study detail record. The prototype stored loose objects; we keep that
 *  shape and allow the runtime caches (_matrixBuilt etc.) it mutates. */
export interface Study {
  mdlId: string; title: string; sub: string; modelType: ModelType; status?: StudyStatus;
  multiAnalyte?: boolean; analytes?: Analyte[];
  nSpike?: number; nBlank?: number; days?: number; tValue?: number;
  kpis: Kpi[];
  spikeCols?: string[]; spikeRows?: Array<Array<string | number>>; spikeFooter?: string;
  blankCols?: string[]; blankRows?: Array<Array<string | number>>; blankFooter?: string;
  calcRows: Array<Array<string | number | boolean>>;
  docRows: string[][];
  analyte?: string; units?: string; finalMdl?: number;
  key?: string;
  /** Pre-rendered HTML for the multi-analyte real summary table (when the
   *  study comes from the API rather than the synthetic prototype matrix). */
  summaryHtml?: string;
  // Raw Results tab (flat per-measurement listing) — feedback2 #4
  _rawSpike?: RawResultRow[]; _rawBlank?: RawResultRow[];
  // runtime caches (mutated in place — mirrors the prototype)
  _matrixBuilt?: boolean; _spikeRows?: MatrixRow[]; _blankRows?: MatrixRow[]; _stats?: MatrixStat[];
  _spikeAmtByType?: Record<string, number[]>;   // per-QCType spike amounts (analyte order) — verification item 2
  _spikeLevels?: SpikeLevel[];   // spiked samples grouped by spike level (QC types sharing a spike profile) — EPA: one SD per level
  _compliance?: MdlCompliance;   // 40 CFR 136 App. B procedure checks (gaps 3.1/3.2/3.3)
  _batchMap?: Record<string, string>;
  _signatures?: Signature[]; _attachments?: Attachment[];
}

/* ════════ basic statistics ════════ */
export function mean(a: number[]): number { return a.reduce((x, y) => x + y, 0) / a.length; }
export function sd(a: number[]): number {
  if (a.length < 2) return 0;
  const m = mean(a);
  return Math.sqrt(a.reduce((s, v) => s + (v - m) * (v - m), 0) / (a.length - 1));
}
export function fmtNum(x: number): string { if (!isFinite(x)) return '—'; return Number(x.toPrecision(3)).toString(); }
/** YYYY-MM-DD for "today" (local), without locale APIs (§C2). */
function isoToday(): string {
  const d = new Date(); const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

/* Student's t, one-sided 99th percentile, by degrees of freedom */
const T99: Record<number, number> = {
  1: 31.821, 2: 6.965, 3: 4.541, 4: 3.747, 5: 3.365, 6: 3.143, 7: 2.998, 8: 2.896, 9: 2.821,
  10: 2.764, 11: 2.718, 12: 2.681, 13: 2.650, 14: 2.624, 15: 2.602, 16: 2.583, 17: 2.567, 18: 2.552, 19: 2.539,
  20: 2.528, 24: 2.492, 29: 2.462, 30: 2.457, 39: 2.426, 40: 2.423, 49: 2.405, 59: 2.391, 99: 2.365,
};
export function tValue(df: number): number {
  const exact = T99[df];
  if (exact != null) return exact;
  const keys = Object.keys(T99).map(Number).sort((a, b) => a - b);
  let t = T99[6] as number;
  for (const k of keys) if (k <= df) t = T99[k] as number;
  return t;
}

/* ════════ deterministic synthetic data (multi-analyte matrix) ════════ */
export function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function gauss(rng: () => number): number {
  let u = 0, v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
function standardize(vals: number[], targetMean: number, targetSd: number): number[] {
  const m = mean(vals), s = sd(vals) || 1;
  return vals.map((x) => targetMean + ((x - m) / s) * targetSd);
}
export function strHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rnd3(x: number): number { return Number(x.toPrecision(3)); }

/** Build a realistic per-analyte list with deterministic pseudo-random MDLs. */
export function mkAnalytes(
  names: string[], unit: string, spike: number | ((i: number, nm: string) => number),
  baseMdl: number, verif: boolean,
): Analyte[] {
  return names.map((nm, i) => {
    const rng = mulberry32(strHash(nm) + 1);
    const mdls = rnd3(baseMdl * (0.55 + rng() * 1.1));
    const mdlb = rnd3(mdls * (0.45 + rng() * 0.35));
    const fin = Math.max(mdls, mdlb);
    const sp = (typeof spike === 'function') ? spike(i, nm) : spike;
    const a: Analyte = { name: nm, unit, spike: sp, mdls, mdlb, final: fin };
    if (verif) {
      const r = rng();
      const mult = r < 0.08 ? (2.0 + rng() * 0.6) : (r > 0.93 ? (0.34 + rng() * 0.13) : (0.6 + rng() * 1.0));
      a.current = rnd3(fin / mult);
      a.ratio = fin / a.current;
      a.status = a.ratio > 2.0 ? 'fail' : (a.ratio < 0.5 ? 'flag' : 'pass');
    }
    return a;
  });
}
function qcBatchId(i: number, seqOffset: number): string {
  const dt = new Date(2024, 0, 8 + i * 5);
  const yy = String(dt.getFullYear()).slice(2);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return 'QB' + yy + mm + dd + '01_' + String(seqOffset + i + 1).padStart(3, '0');
}

export function buildMatrix(d: Study): void {
  if (d._matrixBuilt || !d.analytes) return;
  const t = d.tValue || tValue((d.nSpike || 50) - 1);
  const nS = d.nSpike || 50, nB = d.nBlank || 50;
  const spikeTypes = ['FB', 'LOD', 'LOQ', 'LCS_Low'], blankTypes = ['MB', 'LRB'];

  const spikeCols = d.analytes.map((a, ai) => {
    const rng = mulberry32(1000 + ai * 97); const raw: number[] = [];
    for (let i = 0; i < nS; i++) raw.push(gauss(rng));
    return standardize(raw, a.spike, a.mdls / t);
  });
  const blankCols = d.analytes.map((a, ai) => {
    const rng = mulberry32(5000 + ai * 131); const raw: number[] = [];
    for (let i = 0; i < nB; i++) raw.push(gauss(rng));
    const xbar = a.mdlb * 0.3;
    return standardize(raw, xbar, Math.max((a.mdlb - xbar) / t, a.mdlb * 0.06));
  });

  d._spikeRows = [];
  for (let i = 0; i < nS; i++) d._spikeRows.push({
    id: qcBatchId(i, 0), type: spikeTypes[i % spikeTypes.length] as string,
    vals: d.analytes.map((_, ai) => (spikeCols[ai] as number[])[i] as number),
  });
  d._blankRows = [];
  for (let j = 0; j < nB; j++) d._blankRows.push({
    id: qcBatchId(j, 500), type: blankTypes[j % blankTypes.length] as string,
    vals: d.analytes.map((_, ai) => (blankCols[ai] as number[])[j] as number),
  });

  d._stats = d.analytes.map((a, ai) => {
    const sv = spikeCols[ai] as number[];
    const bv = (blankCols[ai] as number[]).map((x) => (x <= 0 ? 0 : x));
    const Ss = sd(sv), mdlSpk = t * Ss;
    const Xb = mean(bv), Sb = sd(bv), mdlBlk = Xb + t * Sb;
    const Final = Math.max(mdlSpk, mdlBlk);
    const current = (a.current != null) ? a.current : null;
    const ratio = current ? Final / current : null;
    const verif = ratio == null ? null : (ratio > 2.0 ? 'update' : (ratio < 0.5 ? 'low' : 'pass'));
    return {
      avgSpk: mean(sv), nSpk: sv.length, stdSpk: Ss, t, mdlSpk,
      avgBlk: Xb, nBlk: bv.length, stdBlk: Sb, mdlBlk,
      final: Final, current, ratio, verif,
    };
  });
  d._matrixBuilt = true;
}

function verifPill(v: string | null): string {
  const map: Record<string, [string, string]> = { pass: ['sp-pass', '✓ Pass'], update: ['sp-overdue', '✗ Update'], low: ['sp-pending', '⚠ Low'] };
  const m = v ? map[v] : undefined;
  return m ? '<span class="sp ' + m[0] + '">' + m[1] + '</span>' : '<span style="color:#bbb">—</span>';
}
function esc(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
/** Second-column header per section: "SysSampleCode" for spike/blank rows, "Sample Type" for the summary. */
function matrixHeader(d: Study, kind: 'spike' | 'blank' | 'summary'): string {
  const col2 = kind === 'summary' ? 'Sample Type' : 'SysSampleCode';
  let h = '<thead><tr><th class="col-batch">QC Batch ID</th><th class="col-type">' + col2 + '</th>';
  (d.analytes || []).forEach((a) => { h += '<th>' + esc(a.name) + '<span class="an-unit">' + esc(a.unit) + '</span></th>'; });
  return h + '</tr></thead>';
}
/** A statistic row; the second column carries an optional Sample-Type label (summary). */
function statRow(label: string, cls: string, cells: Array<string | number>, typeCell = ''): string {
  let r = '<tr class="' + cls + '"><td class="col-batch">' + label + '</td><td class="col-type">' + typeCell + '</td>';
  cells.forEach((c) => { r += '<td class="num">' + c + '</td>'; });
  return r + '</tr>';
}
function renderMatrixTable(d: Study, kind: 'spike' | 'blank' | 'summary'): string {
  buildMatrix(d);
  const stats = d._stats || [];
  let html = '<table class="mtx">' + matrixHeader(d, kind) + '<tbody>';
  if (kind === 'spike') {
    const sRows = d._spikeRows || [];
    const levels = d._spikeLevels || [];
    const ncol = 2 + (d.analytes || []).length;
    // Group the spiked samples by SPIKE LEVEL (QC types sharing a spike-amount profile pool
    // together — e.g. MS + MSD). Each level shows its own Spike Amount row + statistics.
    const grouped = levels.length > 1;
    const spikeAmtRow = (amts: number[] | undefined): string => {
      let h = '<tr class="mtx-spikeamt"><td class="col-batch">Spike Amount</td><td class="col-type"></td>';
      (d.analytes || []).forEach((a, ai) => { h += '<td class="num">' + fmtNum(amts ? (amts[ai] ?? NaN) : a.spike) + '</td>'; });
      return h + '</tr>';
    };
    const sampleRow = (row: MatrixRow, ri: number): string => {
      const exStyle = row.excluded ? ' style="text-decoration:line-through;color:#B3261E;opacity:.7" title="Excluded outlier — not in statistics"' : '';
      let h = '<tr class="' + (ri % 2 ? 'zebra' : '') + '"' + exStyle + '><td class="col-batch">' + esc(row.id) + (row.excluded ? ' ⊘' : '') + '</td><td class="col-type">' + esc(row.sample || '—') + '</td>';
      row.vals.forEach((v) => { h += '<td class="num">' + fmtNum(v) + '</td>'; });
      return h + '</tr>';
    };
    // Average / Count / Std Dev / t-Value / MDL Spike rows for a given stat set.
    const spikeStatBlock = (st: MatrixStat[]): string =>
      statRow('Average', 'mtx-stat', st.map((s) => fmtNum(s.avgSpk)))
      + statRow('Count', 'mtx-stat', st.map((s) => s.nSpk))
      + statRow('Std Dev', 'mtx-stat', st.map((s) => fmtNum(s.stdSpk)))
      + statRow('t-Value', 'mtx-stat', st.map((s) => s.t.toFixed(3)))
      + statRow('MDL Spike', 'mtx-stat', st.map((s) => fmtNum(s.mdlSpk)));
    if (grouped) {
      let ri = 0;
      levels.forEach((lv) => {
        html += '<tr class="mtx-grouphdr"><td colspan="' + ncol + '" style="background:#f4f6f8;font-weight:bold;color:#185FA5;padding:4px 8px">Spike Level — ' + esc(lv.label) + '</td></tr>';
        html += spikeAmtRow(lv.amts);
        lv.rows.forEach((row) => { html += sampleRow(row, ri++); });
        html += spikeStatBlock(lv.stats);   // valid single-spike-level SD (EPA App. B)
      });
    } else {
      html += spikeAmtRow(levels[0] ? levels[0].amts : undefined);
      sRows.forEach((row, ri) => { html += sampleRow(row, ri); });
      html += spikeStatBlock(stats);
    }
  } else if (kind === 'blank') {
    (d._blankRows || []).forEach((row, ri) => {
      const exStyle = row.excluded ? ' style="text-decoration:line-through;color:#B3261E;opacity:.7" title="Excluded outlier — not in statistics"' : '';
      html += '<tr class="' + (ri % 2 ? 'zebra' : '') + '"' + exStyle + '><td class="col-batch">' + esc(row.id) + (row.excluded ? ' ⊘' : '') + '</td><td class="col-type">' + esc(row.sample || '—') + '</td>';
      // Show the SpreadSheetEntry_SampleInfo.NumericResult value (incl. 0) — only a truly
      // missing value renders as a dash.
      row.vals.forEach((v) => { html += '<td class="num">' + (v == null || !isFinite(v) ? '<span style="color:#bbb">—</span>' : fmtNum(v)) + '</td>'; });
      html += '</tr>';
    });
    html += statRow('Average', 'mtx-stat', stats.map((s) => fmtNum(s.avgBlk)));
    html += statRow('Count', 'mtx-stat', stats.map((s) => s.nBlk));
    html += statRow('Std Dev', 'mtx-stat', stats.map((s) => fmtNum(s.stdBlk)));
    html += statRow('MDL Blank', 'mtx-stat', stats.map((s) => fmtNum(s.mdlBlk)));
  } else {
    // Statistics & MDL Summary — Sample Type column labels the MDL Spike / MDL Blank rows.
    html += statRow('MDL Spike', 'mtx-stat', stats.map((s) => fmtNum(s.mdlSpk)), 'Spiked Sample');
    html += statRow('MDL Blank', 'mtx-stat', stats.map((s) => fmtNum(s.mdlBlk)), 'Method Blank');
    html += statRow('Final MDL', 'mtx-final', stats.map((s) => fmtNum(s.final)));
    html += statRow('Current MDL', 'mtx-stat', stats.map((s) => (s.current != null ? fmtNum(s.current) : '—')));
    // §4 second criterion (gap 2.2): trailing-24-month method-blank exceedance vs the current MDL.
    if (stats.some((s) => s.blankTotal != null)) {
      let er = '<tr class="mtx-stat"><td class="col-batch">2-yr Blanks &gt; MDL</td><td class="col-type"><span style="font-size:10px;color:#888">&lt; 3% to retain</span></td>';
      stats.forEach((s) => {
        if (s.blankTotal == null) { er += '<td style="text-align:center;color:#bbb">—</td>'; return; }
        const pct = s.blankTotal ? (s.blankExceed ?? 0) / s.blankTotal * 100 : 0;
        const col = s.blankExceedOk === false ? '#B3261E' : '#0F6E56';
        er += '<td style="text-align:center;color:' + col + '">' + (s.blankExceed ?? 0) + '/' + s.blankTotal + ' (' + pct.toFixed(1) + '%)</td>';
      });
      html += er + '</tr>';
    }
    let vr = '<tr class="mtx-stat"><td class="col-batch">Verification</td><td class="col-type"></td>';
    stats.forEach((s) => { vr += '<td style="text-align:center">' + (s.ratio != null ? verifPill(s.verif) : '<span style="color:#bbb">—</span>') + '</td>'; });
    html += vr + '</tr>';
  }
  return html + '</tbody></table>';
}
function matrixSection(title: string, sub: string, inner: string): string {
  return '<div style="margin:0 0 16px">' +
    '<div style="font-size:12px;font-weight:bold;color:#333;padding:4px 2px">' + title +
    (sub ? ' <span style="font-weight:normal;color:#999;font-size:11px">· ' + sub + '</span>' : '') + '</div>' +
    '<div class="mtx-wrap">' + inner + '</div></div>';
}
/** One labelled matrix section (used standalone in the study-detail tabs). */
export function renderMatrixSection(d: Study, kind: 'spike' | 'blank' | 'summary'): string {
  buildMatrix(d);
  const isVerif = d.modelType === 'verif';
  const nSamples = (kind === 'blank' ? d._blankRows : d._spikeRows || [])?.length || 0;
  if (kind === 'spike') return matrixSection('Spiked Samples', nSamples + ' samples · ' + (d.analytes || []).length + ' analytes', renderMatrixTable(d, 'spike'));
  if (kind === 'blank') return matrixSection('Method Blanks', nSamples + ' samples · NumericResult', renderMatrixTable(d, 'blank'));
  return matrixSection('Statistics & MDL Summary', isVerif ? 'Verification vs Current MDL (§4f: 0.5×–2.0× = Pass)' : 'Final MDL = greater of MDL Spike / MDL Blank', renderMatrixTable(d, 'summary'));
}
function renderMatrixView(d: Study): string {
  return renderMatrixSection(d, 'spike') + renderMatrixSection(d, 'blank') + renderMatrixSection(d, 'summary');
}

/* ════════ single-analyte spike / blank tables ════════ */
function qcBatchId2(d: Study, rawBatch: string, dateStr: string): string {
  d._batchMap = d._batchMap || {};
  if (d._batchMap[rawBatch]) return d._batchMap[rawBatch];
  const n = Object.keys(d._batchMap).length + 1;
  const p = (dateStr || '').split('-');
  const yy = p[0] ? p[0].slice(2) : '00', mm = p[1] || '00', dd = p[2] || '00';
  const id = 'QB' + yy + mm + dd + '01_' + String(n).padStart(3, '0');
  d._batchMap[rawBatch] = id;
  return id;
}
export function fmtCalcRow(r: Array<string | number | boolean>): string {
  const highlight = r[3] ? ' calc-highlight' : '';
  const lbl = r[3] ? '✦ ' + r[0] : String(r[0]);
  return '<div class="calc-row' + highlight + '"><span class="calc-lbl">' + lbl + '</span><span class="calc-val">' + r[1] + '</span>' + (r[2] ? '<span class="calc-note">' + r[2] + '</span>' : '') + '</div>';
}
export function renderSpikeTable(d: Study): string {
  if (d.multiAnalyte) return renderMatrixView(d);
  const cols = d.spikeCols || [], rows = d.spikeRows || [];
  let html = '<table class="xaf-table"><thead><tr>';
  cols.forEach((c) => { html += '<th>' + c + '</th>'; });
  html += '</tr></thead><tbody>';
  if (rows.length === 0) html += '<tr><td colspan="' + cols.length + '" style="text-align:center;color:#aaa;padding:24px">No data retrieved yet</td></tr>';
  rows.forEach((r) => {
    html += '<tr>';
    for (let i = 0; i < r.length - 1; i++) {
      const cls = (i === 1 || i === 5) ? ' class="mono"' : '';
      const val = (i === 1) ? qcBatchId2(d, String(r[1]), String(r[3])) : r[i];
      html += '<td' + cls + '>' + val + '</td>';
    }
    html += '<td><span class="sp sp-pass">Pass</span></td></tr>';
  });
  return html + '</tbody></table>';
}
export function renderBlankTable(d: Study): string {
  if (d.multiAnalyte) return '<div style="padding:20px;text-align:center;color:#888;font-size:11px">Blank data is integrated per-analyte — see Spiked Samples tab for combined MDL<sub>s</sub> / MDL<sub>b</sub> results.</div>';
  const cols = d.blankCols || [], rows = d.blankRows || [];
  let html = '<table class="xaf-table"><thead><tr>';
  cols.forEach((c) => { html += '<th>' + c + '</th>'; });
  html += '</tr></thead><tbody>';
  if (rows.length === 0) html += '<tr><td colspan="' + cols.length + '" style="text-align:center;color:#aaa;padding:24px">No data retrieved yet</td></tr>';
  rows.forEach((r) => {
    html += '<tr>';
    html += '<td>' + r[0] + '</td><td class="mono">' + qcBatchId2(d, String(r[1]), String(r[3])) + '</td><td>' + r[2] + '</td><td>' + r[3] + '</td>';
    html += '<td class="mono">' + r[4] + '</td>';
    html += '<td>' + (r[5] === 'yes' ? '<i class="ti ti-check" style="color:#0F6E56"></i> Yes' : r[5]) + '</td>';
    html += '<td>' + (r[6] ? '<span style="color:#854F0B">' + r[6] + '</span>' : '') + '</td>';
    html += '</tr>';
  });
  return html + '</tbody></table>';
}

/* ════════ KPI strip / doc rows ════════ */
export function renderKpis(d: Study): string {
  let html = '';
  d.kpis.forEach((k) => {
    const border = k.accent ? 'border-left:4px solid #e8670a' : '';
    const valStyle = k.color ? 'style="color:' + k.color + '"' : '';
    const cls = k.cls ? ' ' + k.cls : '';
    html += '<div class="kpi" style="' + border + '">';
    html += '<div class="kpi-lbl">' + k.lbl + '</div>';
    html += '<div class="kpi-val' + cls + '" ' + valStyle + '>' + k.val + (k.unit ? ' <span style="font-size:14px">' + k.unit + '</span>' : '') + '</div>';
    html += '<div class="kpi-sub">' + (k.sub || '') + '</div>';
    html += '</div>';
  });
  return html;
}
export function renderCalcRows(d: Study): string { return d.calcRows.map(fmtCalcRow).join(''); }
export function renderDocRows(d: Study): string {
  return d.docRows.map((r) => '<div class="calc-row"><span class="calc-lbl">' + r[0] + '</span><span class="calc-val">' + r[1] + '</span></div>').join('');
}

/* ════════ attachments ════════ */
export function fmtBytes(b: number | null): string {
  if (b == null) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function fileIcon(name: string): string {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const map: Record<string, string> = {
    pdf: 'ti-file-type-pdf', csv: 'ti-file-spreadsheet', xlsx: 'ti-file-spreadsheet', xls: 'ti-file-spreadsheet',
    txt: 'ti-file-text', raw: 'ti-file-analytics', d: 'ti-file-analytics', png: 'ti-photo', jpg: 'ti-photo', jpeg: 'ti-photo', zip: 'ti-file-zip',
  };
  return map[ext] || 'ti-file';
}
/** Returns attachments table HTML. Remove buttons carry data-idx for the
 *  React layer to delegate clicks. */
export function renderAttachments(d: Study | undefined): string {
  const list = (d && d._attachments) || [];
  if (!list.length) return '<div style="padding:8px 14px;color:#aaa;font-size:11px">No files attached yet.</div>';
  let h = '<table class="xaf-table"><thead><tr><th>File</th><th>Type</th><th>Size</th><th>Attached By</th><th>Date / Time</th><th></th></tr></thead><tbody>';
  list.forEach((a, i) => {
    const key = a.id ?? String(i);
    const nameCell = a.id
      ? '<a data-attach-download="' + a.id + '" style="color:#185FA5;cursor:pointer" title="Download">' + a.name + '</a>'
      : a.name;
    h += '<tr><td><i class="ti ' + fileIcon(a.name) + '" style="color:#185FA5;vertical-align:-2px"></i> ' + nameCell +
      '</td><td style="color:#888">' + a.type + '</td><td class="mono">' + fmtBytes(a.size) +
      '</td><td>' + (a.by || '—') + '</td><td class="mono">' + fmtSigDate(a.when) +
      '</td><td style="text-align:center"><a data-attach-remove="' + key + '" style="color:#A32D2D;cursor:pointer" title="Remove"><i class="ti ti-trash"></i></a></td></tr>';
  });
  return h + '</tbody></table>';
}

/* ════════ e-signature engine ════════ */
export function userIdFor(name: string): string { return name.toLowerCase().replace(/\s+/g, '').replace(/dr\./, ''); }

function pseudoSha256(str: string): string {
  function h(seed: number): number {
    let x = seed >>> 0;
    for (let i = 0; i < str.length; i++) { x ^= str.charCodeAt(i); x = Math.imul(x, 16777619) >>> 0; x = ((x << 13) | (x >>> 19)) >>> 0; }
    return x >>> 0;
  }
  const seeds = [2166136261, 0x811c9dc5, 0xdeadbeef, 0xcafebabe, 0x12345678, 0x9e3779b9, 0x7f4a7c15, 0x85ebca6b];
  return seeds.map((s) => ('00000000' + h(s).toString(16)).slice(-8)).join('');
}
export function recordHashFor(d: Study, status: StudyStatus): string {
  const snap = {
    id: d.mdlId, status: (status || d.status || 'draft'),
    kpis: d.kpis, calc: d.calcRows,
    sigs: (d._signatures || []).map((s) => s.action + '|' + s.userId + '|' + fmtSigDate(s.when)),
  };
  return pseudoSha256(JSON.stringify(snap));
}
function shortHash(h: string | undefined): string { return h ? (h.slice(0, 10) + '…') : '—'; }
export function fmtSigDate(w: Date | string): string {
  if (w instanceof Date) {
    const p = (n: number) => String(n).padStart(2, '0');
    return w.getFullYear() + '-' + p(w.getMonth() + 1) + '-' + p(w.getDate()) + ' ' + p(w.getHours()) + ':' + p(w.getMinutes());
  }
  return w;
}

const REPORT_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** Long report date — e.g. "July 6, 2026" (built from parts; no toLocale, per C2). */
export function fmtLongDate(w: Date): string {
  return REPORT_MONTHS[w.getMonth()] + ' ' + w.getDate() + ', ' + w.getFullYear();
}

/** Short report date — MM/DD/YYYY (built from parts; no toLocale, per C2 / §14b.6). */
export function fmtReportDate(w: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return p(w.getMonth() + 1) + '/' + p(w.getDate()) + '/' + w.getFullYear();
}

/** Laboratory identity for the report letterhead (only the lab's identity appears on
 *  the report — never the LIMS product name). */
const LAB_ORG = 'City of Houston';                     // parent org (the seal logo)
const LAB_NAME = 'Drinking Water Regulatory Compliance Laboratory';   // the laboratory
const LAB_ADDRESS = ['1770 Sidney St', 'Houston, TX 77002'];
export function ensureSeedSignatures(d: Study, status: StudyStatus): void {
  if (d._signatures) return;
  let created = ''; (d.docRows || []).forEach((r) => { if (r[0] === 'Created') created = r[1] ?? ''; });
  const parts = created.split('·');
  const cDate = (parts[0] || '').replace(/&nbsp;/g, '').trim() || '2025-01-01';
  const cName = (parts[1] || '').replace(/&nbsp;/g, '').trim() || 'S. Demo';
  const sigs: Signature[] = [{ action: 'Submit', meaning: 'Authored & Submitted', name: cName, userId: userIdFor(cName), role: 'Analyst', mode: 'iso', when: cDate + ' 09:14', reason: '', seeded: true }];
  const rank = ({ pending: 0, draft: 0, validated: 1, approved: 2, released: 3 } as Record<string, number>)[status] || 0;
  if (rank >= 1) sigs.push({ action: 'Validate', meaning: 'Reviewed & Validated', name: 'S. Park', userId: 's.park', role: 'Reviewer', mode: 'iso', when: cDate + ' 11:32', reason: '', seeded: true });
  if (rank >= 2) sigs.push({ action: 'Approve', meaning: 'Approved', name: 'Dr. N. Rao', userId: 'n.rao', role: 'QA Manager', mode: 'iso', when: cDate + ' 15:08', reason: '', seeded: true });
  if (rank >= 3) sigs.push({ action: 'Release', meaning: 'Released', name: 'System (auto)', userId: 'system', role: 'Lab Director', mode: 'iso', when: cDate + ' 15:09', reason: 'Released to production after approval', seeded: true });
  sigs.forEach((s) => { s.mfa = false; s.recordHash = pseudoSha256((d.mdlId || '') + '|' + s.action + '|' + s.when); });
  d._signatures = sigs;
}
function sigActionPill(action: string): string {
  const c = ({ Submit: 'sp-initial', Validate: 'sp-validated', Approve: 'sp-approved', Release: 'sp-released' } as Record<string, string>)[action] || 'sp-draft';
  return '<span class="sp ' + c + '">' + action + '</span>';
}
function modePill(m: string): string {
  return (m === 'part11') ? '<span class="sp sp-validated">21 CFR Part 11</span>' : '<span class="sp sp-approved">ISO 17025</span>';
}
export function renderSignatures(d: Study | undefined): string {
  if (!d || !d._signatures || !d._signatures.length) return '';
  let h = '<table class="xaf-table"><thead><tr><th>Action</th><th>Meaning of Signature</th><th>Signed By</th><th>e-Signature</th><th>Role</th><th>User ID</th><th>Date / Time</th><th>Standard</th><th>Integrity (SHA-256)</th><th>Reason</th></tr></thead><tbody>';
  d._signatures.forEach((s) => {
    const std = modePill(s.mode) + (s.mfa ? ' <span style="font-size:9px;color:#0F6E56;font-weight:bold">+MFA</span>' : '');
    h += '<tr><td>' + sigActionPill(s.action) + '</td><td>' + s.meaning + '</td><td style="font-weight:bold">' + s.name +
      '</td><td><span class="esig-mark">/s/ ' + s.name + '</span></td><td>' + (s.role || '—') + '</td><td class="mono">' + s.userId + '</td><td class="mono">' + fmtSigDate(s.when) +
      '</td><td>' + std + '</td><td class="mono" style="color:#0F6E56" title="' + (s.recordHash || '') + '"><i class="ti ti-fingerprint" style="vertical-align:-2px"></i> ' + shortHash(s.recordHash) +
      '</td><td style="color:#666">' + (s.reason || '—') + '</td></tr>';
  });
  h += '</tbody></table><div style="padding:6px 12px;background:#fafafa;border-top:1px solid #eee;font-size:10px;color:#888">' +
    '<i class="ti ti-lock" style="vertical-align:-2px"></i> Name, date/time, meaning and authority are recorded for every signing (ISO/IEC 17025). Each signature stores a <strong>hash snapshot</strong> of the record state (tamper-evident). <strong>21 CFR Part 11</strong> entries additionally captured two-component auth + MFA and are bound per §11.70 — none can be altered or removed.</div>';
  return h;
}

export function studyHasData(d: Study | undefined): boolean {
  if (!d) return false;
  if (d.multiAnalyte) return (d.analytes || []).length > 0;
  return (d.spikeRows || []).length > 0;
}

/* ════════ build a new study from retrieved SDMS data ════════ */
export interface NewStudyQuery {
  matrix: string; matrixFull: string; test?: string; testLabel?: string;
  method: string; instrument: string; params: string[]; modelType: ModelType;
  refVal: number | null; refId: string | null;
}
export interface RetrieveSpike { batch: string; sample: string; date: string; spike: number; result: number; }
export interface RetrieveBlank { batch: string; sample: string; date: string; result: number; }

export function buildStudyFromRetrieve(
  q: NewStudyQuery, spikesIn: RetrieveSpike[], blanksIn: RetrieveBlank[], counter: number,
): Study & { key: string } {
  const units = 'mg/L';
  const analyte = q.params.length ? q.params[0] : (q.testLabel || 'Analyte');

  const spikes = spikesIn.filter((s) => isFinite(s.result));
  const blanks = blanksIn.filter((b) => isFinite(b.result));

  const results = spikes.map((s) => s.result);
  const spikeLevel = spikes.length ? (spikes[0] as RetrieveSpike).spike : 0;
  const n = spikes.length, df = Math.max(1, n - 1), t = tValue(df);
  const Ss = sd(results), MDLs = t * Ss;
  const meanRec = spikeLevel ? (mean(results) / spikeLevel) * 100 : 0;

  const blankVals = blanks.map((b) => b.result);
  const blankMean = blankVals.length ? mean(blankVals) : 0;
  const Sb = sd(blankVals);
  const MDLb = blankMean + t * Sb;
  const Final = Math.max(MDLs, MDLb);

  const ratio = (q.modelType === 'verif' && q.refVal) ? (Final / q.refVal) : null;
  const year = new Date().getFullYear();
  const mdlId = 'MDL-' + year + '-' + String(100 + counter);
  const key = 'NEW-' + counter;
  const typeLabel = q.modelType === 'verif' ? 'MDL Verification' : 'Initial MDL';

  const kpis: Kpi[] = [
    { lbl: 'Final MDL', val: fmtNum(Final), unit: units, cls: 'orange', sub: 'Greater of MDL<sub>s</sub> / MDL<sub>b</sub>', accent: true },
    { lbl: 'MDL<sub>s</sub> — Spiked', val: fmtNum(MDLs), unit: units, cls: 'blue', sub: 't × S<sub>s</sub>' },
    { lbl: 'MDL<sub>b</sub> — Blanks', val: fmtNum(MDLb), unit: units, cls: '', color: '#534AB7', sub: 'X̄ + t × S<sub>b</sub>' },
    (ratio != null
      ? { lbl: 'Ratio to Current MDL', val: ratio.toFixed(2) + '×', unit: '', cls: (ratio >= 0.5 && ratio <= 2.0 ? 'green' : 'red'), sub: (ratio >= 0.5 && ratio <= 2.0 ? 'Within 0.5×–2.0× — Pass' : 'Outside 0.5×–2.0× — Review') }
      : { lbl: 't-value (df=' + df + ')', val: fmtNum(t), unit: '', cls: 'green', sub: 'n=' + n + ', single-tail' }),
  ];

  const calcRows: Array<Array<string | number | boolean>> = [
    ['Regulation', '40 CFR 136 Appendix B, Revision 2', ''],
    ['Study Type', typeLabel, 'Computed from retrieved SDMS data'],
    ['Matrix', q.matrixFull, ''],
    ['Spiking Level', fmtNum(spikeLevel) + ' ' + units, ''],
    ['n (spiked samples)', n, n >= 7 ? 'Minimum required: 7 ✓' : 'Below minimum (7) ✗'],
    ['n (method blanks)', blanks.length, ''],
    ['Degrees of Freedom (df)', df, 'n − 1'],
    ['t-value (df=' + df + ', 99th pct, single-tail)', fmtNum(t), 'Per Addendum Table 1'],
    ['S<sub>s</sub> — Spiked std dev', fmtNum(Ss) + ' ' + units, ''],
    ['MDL<sub>s</sub> = t × S<sub>s</sub>', fmtNum(MDLs) + ' ' + units, fmtNum(t) + ' × ' + fmtNum(Ss)],
    ['Blank condition', 'All ' + blanks.length + ' blanks returned numerical results', 'Full formula applies (§2d·iii·C)'],
    ['X̄ — Blank mean', fmtNum(blankMean) + ' ' + units, ''],
    ['S<sub>b</sub> — Blank std dev', fmtNum(Sb) + ' ' + units, ''],
    ['MDL<sub>b</sub> = X̄ + t × S<sub>b</sub>', fmtNum(MDLb) + ' ' + units, ''],
  ];
  if (ratio != null) {
    calcRows.push(['Reference MDL', (q.refId ? q.refId + ' · ' : '') + fmtNum(q.refVal as number) + ' ' + units, 'Existing released MDL']);
    calcRows.push(['Ratio to Current MDL', ratio.toFixed(2) + '×', (ratio >= 0.5 && ratio <= 2.0) ? 'Within 0.5×–2.0× (§4f)' : 'Outside 0.5×–2.0× — MDL must be updated (§4f)']);
  }
  calcRows.push(['Final MDL = max(MDL<sub>s</sub>, MDL<sub>b</sub>)', fmtNum(Final) + ' ' + units, (MDLb >= MDLs ? 'MDL<sub>b</sub>' : 'MDL<sub>s</sub>') + ' is greater — selected', true]);

  return {
    key, mdlId,
    title: mdlId + ' — ' + analyte + (q.method ? ' · ' + q.method : '') + (q.instrument ? ' · ' + q.instrument : ''),
    sub: typeLabel + ' · ' + q.matrix + ' Matrix · 40 CFR 136 Appendix B, Rev 2 — Draft (computed)',
    modelType: q.modelType, status: 'draft',
    analyte, units, finalMdl: Final, nSpike: n, nBlank: blanks.length,
    kpis,
    spikeCols: ['#', 'Batch ID', 'SysSampleCode', 'Analysis Date', 'Spike Level (' + units + ')', 'Result (' + units + ')', 'Recovery %', 'QC Flag'],
    spikeRows: spikes.map((s, i) => [i + 1, s.batch, s.sample, s.date, fmtNum(s.spike), fmtNum(s.result), (s.spike ? (s.result / s.spike * 100).toFixed(1) : '—') + '%', 'pass']),
    spikeFooter: 'Mean recovery: <strong style="color:#222">' + meanRec.toFixed(1) + '%</strong>&nbsp;&nbsp;&nbsp; S<sub>s</sub>: <strong style="color:#222">' + fmtNum(Ss) + ' ' + units + '</strong>&nbsp;&nbsp;&nbsp; MDL<sub>s</sub> = ' + fmtNum(t) + ' × ' + fmtNum(Ss) + ' = <strong style="color:#185FA5">' + fmtNum(MDLs) + ' ' + units + '</strong>',
    blankCols: ['#', 'Batch ID', 'SysSampleCode', 'Date', 'Result (' + units + ')', 'Numerical?', 'Note'],
    blankRows: blanks.map((b, i) => [i + 1, b.batch, b.sample, b.date, fmtNum(b.result), 'yes', '']),
    blankFooter: 'X̄: <strong style="color:#222">' + fmtNum(blankMean) + ' ' + units + '</strong>&nbsp;&nbsp;&nbsp; S<sub>b</sub>: <strong style="color:#222">' + fmtNum(Sb) + ' ' + units + '</strong>&nbsp;&nbsp;&nbsp; MDL<sub>b</sub> = X̄ + t × S<sub>b</sub> = <strong style="color:#534AB7">' + fmtNum(MDLb) + ' ' + units + '</strong>',
    calcRows,
    docRows: [
      ['Analytical Method', q.method || '—'],
      ['Sample Matrix', q.matrixFull],
      ['Instrument', q.instrument || '—'],
      ['Reporting Units', units],
      ['Mean Spiked Level', fmtNum(spikeLevel) + ' ' + units],
      ['Mean Recovered', fmtNum(mean(results)) + ' ' + units + ' (' + meanRec.toFixed(1) + '%)'],
      ['Outliers Excluded', 'None'],
      ['Created', isoToday() + ' &nbsp;·&nbsp; S. Demo'],
    ],
  };
}

/* ════════ real SDMS retrieve → multi-analyte matrix ════════
 *  Pivots the flat qualifying SampleParameter rows returned by
 *  GET /api/v1/mdl-studies/retrieve (LFB = spiked 'S' · LRB = blank 'B')
 *  into a multi-analyte Study whose matrix caches are pre-filled, so the
 *  existing renderMatrixView() draws the MDL-2025-009-style matrix from REAL
 *  data instead of the synthetic prototype generator. */
export interface RetrieveFlatRow {
  analyte: string; qcBatchId: string; sampleId: string; sysSampleCode: string; result: string; kind: string;   // 'S' | 'B'
  /** Samplelogin.SysSampleCode — the QC sample as LOGGED IN (the row flagged as an
   *  LFB / LRB category). Distinct from sysSampleCode, which is the QC POSITION
   *  inside the analytical run ('LCS Digested1') and is therefore a QC type. */
  loginSampleCode?: string;
  qcTypeName?: string;   // Verification: TestParameter.QCType.QCTypeName
  value: number | null; spikeAmount: number | null; currentMdl: number | null; units: string | null; analyzedDate: string | null;
}
export interface RetrieveMeta {
  matrix: string; test: string; method: string; instrument: string;
  modelType: ModelType; dateFrom: string; dateTo: string;
}

/** Compute the per-analyte matrix statistics from the pivoted spike/blank rows.
 *  Shared by the retrieve matrix and the multi-analyte study-detail matrix.
 *  Spike rows already hold % recovery (feedback2 #2); Current MDL = TestParameter.MDL
 *  and (when withVerif) the verification ratio = Final / Current MDL. */
export function computeMatrixStats(analytes: Analyte[], spikeRows: MatrixRow[], blankRows: MatrixRow[], withVerif = false): MatrixStat[] {
  const nS = spikeRows.length, nB = blankRows.length;
  const tS = tValue(Math.max(1, nS - 1)), tB = tValue(Math.max(1, nB - 1));
  return analytes.map((a, ai) => {
    const sv = spikeRows.map((r) => r.vals[ai]).filter((v): v is number => v != null && isFinite(v));
    const Ss = sd(sv), mdlSpk = nS >= 2 ? tS * Ss : 0;
    // Blank MDL — EPA 40 CFR 136 App. B three-case rule (gap 2.1):
    //   none numerical → MDLb not used · SOME (not all) numerical → highest blank result ·
    //   ALL numerical → X̄blank + t·Sblank.  "numerical" = a detected value for this analyte;
    //   a non-detect leaves vals[ai] NaN and is excluded but still counts toward the blank total nB.
    const numericBlanks = blankRows.map((r) => r.vals[ai]).filter((v): v is number => v != null && isFinite(v));
    const bv = numericBlanks.map((x) => (x <= 0 ? 0 : x));
    const Xb = bv.length ? mean(bv) : 0, Sb = sd(bv);
    let mdlBlk: number;
    if (numericBlanks.length === 0) mdlBlk = 0;                       // Case 1 — MDLb not used
    else if (numericBlanks.length > 100) {                           // C11 — 99th-percentile rule
      // When >100 method-blank results are available, MDLb = 99th percentile of the
      // blank results (nearest-rank), per 40 CFR 136 App. B Rev. 2 (feedback 2026-06-26).
      const sorted = [...bv].sort((x, y) => x - y);
      const rank = Math.min(sorted.length, Math.max(1, Math.ceil(0.99 * sorted.length)));   // 1-based
      mdlBlk = sorted[rank - 1]!;
    }
    else if (numericBlanks.length < nB) mdlBlk = Math.max(...bv);     // Case 2 — highest method-blank result
    else mdlBlk = Xb + tB * Sb;                                       // Case 3 — X̄blank + t·Sblank
    const final = Math.max(mdlSpk, mdlBlk);
    const current = a.current != null ? a.current : null;
    const ratio = (withVerif && current != null && current > 0) ? final / current : (a.ratio ?? null);
    // §4 retain decision = ratio within 0.5×–2.0× AND <3% of trailing-24-month blanks exceed the current MDL (gap 2.2).
    const blankTotal = a.blankTotal, blankExceed = a.blankExceed;
    const blankExceedOk: boolean | null = (blankTotal != null && blankTotal > 0)
      ? (blankExceed ?? 0) / blankTotal < 0.03 : null;   // null = no 2-yr blank data → criterion not evaluable
    let verif: string | null;
    if (!withVerif || ratio == null) verif = a.status ?? null;
    else if (ratio > 2.0) verif = 'update';
    else if (ratio < 0.5) verif = 'low';
    else verif = (blankExceedOk === false) ? 'update' : 'pass';   // within band but blanks fail → replace
    return {
      avgSpk: sv.length ? mean(sv) : 0, nSpk: sv.length, stdSpk: Ss, t: tS, mdlSpk,
      avgBlk: Xb, nBlk: bv.length, stdBlk: Sb, mdlBlk,
      final, current, ratio, verif, blankTotal, blankExceed, blankExceedOk,
    };
  });
}

export function buildRetrieveMatrixStudy(rows: RetrieveFlatRow[], meta: RetrieveMeta, blankExc?: Record<string, { total: number; exceed: number }>, excluded?: Set<string>): Study {
  // 1. distinct analytes (first-seen order) + their unit + collected spike amounts + current MDL
  const order: string[] = [];
  const unit: Record<string, string> = {};
  const spikeAmts: Record<string, number[]> = {};
  const curMdl: Record<string, number | null> = {};
  // Spike Amount is keyed by (QCType, Parameter) in TestParameter — collect per QC type so the
  // Spiked Samples matrix can show the correct amount for each QC-type group (verification item 2).
  const spikeAmtByType: Record<string, Record<string, number>> = {};
  for (const r of rows) {
    if (!r.analyte) continue;
    if (!(r.analyte in unit)) { order.push(r.analyte); unit[r.analyte] = r.units || ''; spikeAmts[r.analyte] = []; curMdl[r.analyte] = null; }
    if (r.spikeAmount != null && isFinite(r.spikeAmount)) {
      spikeAmts[r.analyte]!.push(r.spikeAmount);
      const qt = r.qcTypeName || '';
      (spikeAmtByType[qt] ??= {})[r.analyte] = r.spikeAmount;   // constant within (qctype, parameter)
    }
    if (curMdl[r.analyte] == null && r.currentMdl != null && isFinite(r.currentMdl)) curMdl[r.analyte] = r.currentMdl;
  }
  const idx = new Map(order.map((nm, i) => [nm, i]));

  // 2. one matrix row per SAMPLE (key = sampleId), columns = analytes; cell value = NumericResult
  const mkRow = (batch: string, sample: string, kind: string, qcType: string): MatrixRow =>
    ({ id: batch, type: kind, sample, qcType, vals: new Array(order.length).fill(NaN) });
  const spikeBySample = new Map<string, MatrixRow>(), blankBySample = new Map<string, MatrixRow>();
  for (const r of rows) {
    if (!r.analyte) continue;
    const k = r.kind === 'S' ? 'S' : r.kind === 'B' ? 'B' : '';
    if (!k) continue;
    const ai = idx.get(r.analyte); if (ai == null) continue;
    // Collapse a sample and its re-reads (…-001 + …-001R1) into one row by the base SysSampleCode.
    const sampleLabel = baseSysCode(r.sysSampleCode) || r.sampleId || '—';
    const qt = r.qcTypeName || '';
    const sampleKey = (qt ? qt + '|' : '') + sampleLabel + '|' + (r.qcBatchId || '');
    const m = k === 'S' ? spikeBySample : blankBySample;
    let row = m.get(sampleKey);
    if (!row) { row = mkRow(r.qcBatchId || '—', sampleLabel, k === 'S' ? 'LFB' : 'LRB', qt); m.set(sampleKey, row); }
    if (r.value != null && isFinite(r.value)) row.vals[ai] = r.value;
  }
  const spikeRows = [...spikeBySample.values()];
  const blankRows = [...blankBySample.values()];
  // Outlier exclusion (gap 3.4): mark excluded replicates; they stay visible (struck-through) but
  // are dropped from every statistic + the n-counts. computeMatrixStats is fed only active rows.
  if (excluded && excluded.size) {
    for (const r of spikeRows) if (excluded.has(replicateKey(r))) r.excluded = true;
    for (const r of blankRows) if (excluded.has(replicateKey(r))) r.excluded = true;
  }
  const activeSpike = spikeRows.filter((r) => !r.excluded);
  const activeBlank = blankRows.filter((r) => !r.excluded);
  const nS = activeSpike.length, nB = activeBlank.length;

  // 3. per-analyte attributes: MDLSpikeAmount (Spike Amount row) + Current MDL (TestParameter.MDL)
  const analytes: Analyte[] = order.map((nm) => {
    const amts = spikeAmts[nm]!;
    const spike = amts.length ? mean(amts) : NaN;                 // MDLSpikeAmount; NaN → rendered as '—'
    const a: Analyte = { name: nm, unit: unit[nm] || '', spike, mdls: 0, mdlb: 0, final: 0 };
    if (curMdl[nm] != null) a.current = curMdl[nm]!;
    const be = blankExc?.[nm];                       // trailing-24-month blank exceedance (gap 2.2)
    if (be) { a.blankTotal = be.total; a.blankExceed = be.exceed; }
    return a;
  });

  // Resolve per-QC-type spike amounts to analyte-order arrays (verification item 2).
  const spikeAmtByTypeArr: Record<string, number[]> = {};
  for (const qt of Object.keys(spikeAmtByType)) spikeAmtByTypeArr[qt] = order.map((nm) => spikeAmtByType[qt]![nm] ?? NaN);

  // Spiked Samples show the raw NumericResult (reverted from % recovery, 2026-06-22).
  // EPA 40 CFR 136 App. B requires the spike SD to be computed from replicates at ONE
  // spike level. Group QC types that share the SAME spike-amount profile into one spike
  // LEVEL (e.g. MS + MSD, both 4.0 → pooled); a different level (e.g. LOQ at 1.0) stays
  // its own group. Statistics are computed per level (verification item b / "per spike level").
  const isVerifMode = meta.modelType === 'verif';
  const levelSig = (amts: number[]) => amts.map((v) => (v == null || !isFinite(v) ? 'x' : Math.round(v * 1e6) / 1e6)).join('|');
  const spikeQcTypes = [...new Set(spikeRows.map((r) => r.qcType || ''))];
  const levelOrder: string[] = [];
  const levelByKey = new Map<string, { qcTypes: string[]; amts: number[] }>();
  spikeQcTypes.forEach((qt) => {
    const amts = spikeAmtByTypeArr[qt] || order.map(() => NaN);
    const key = levelSig(amts) || qt;
    if (!levelByKey.has(key)) { levelByKey.set(key, { qcTypes: [], amts }); levelOrder.push(key); }
    levelByKey.get(key)!.qcTypes.push(qt);
  });
  const spikeLevels: SpikeLevel[] = levelOrder.map((key) => {
    const g = levelByKey.get(key)!;
    const lrows = spikeRows.filter((r) => g.qcTypes.includes(r.qcType || ''));   // all (incl excluded) for display
    return { label: g.qcTypes.filter(Boolean).join(', ') || '—', qcTypes: g.qcTypes, amts: g.amts, rows: lrows,
      stats: computeMatrixStats(analytes, lrows.filter((r) => !r.excluded), activeBlank, isVerifMode) };
  });
  // Overall stats for the Summary / Final MDL: one spike level → use it; multiple levels →
  // take the most conservative (largest MDL Spike) level per analyte, blanks pooled.
  let stats: MatrixStat[];
  if (spikeLevels.length > 1) {
    const pooled = computeMatrixStats(analytes, activeSpike, activeBlank, isVerifMode);   // blank fields + structure
    stats = analytes.map((a, ai) => {
      let best = spikeLevels[0]!.stats[ai]!;
      for (const lv of spikeLevels) { const s = lv.stats[ai]!; if (s.mdlSpk > best.mdlSpk) best = s; }
      const blk = pooled[ai]!;
      const final = Math.max(best.mdlSpk, blk.mdlBlk);
      const current = a.current != null ? a.current : null;
      const ratio = (isVerifMode && current != null && current > 0) ? final / current : null;
      const verif = ratio != null ? (ratio > 2.0 ? 'update' : ratio < 0.5 ? 'low' : 'pass') : null;
      return { avgSpk: best.avgSpk, nSpk: best.nSpk, stdSpk: best.stdSpk, t: best.t, mdlSpk: best.mdlSpk,
        avgBlk: blk.avgBlk, nBlk: blk.nBlk, stdBlk: blk.stdBlk, mdlBlk: blk.mdlBlk,
        final, current, ratio, verif };
    });
  } else {
    stats = computeMatrixStats(analytes, activeSpike, activeBlank, isVerifMode);
  }

  // Raw Results rows (flat) — NumericResult shown raw (not recovery).
  const toRaw = (r: RetrieveFlatRow): RawResultRow => ({
    sysSampleCode: r.sysSampleCode || '', qcBatchId: r.qcBatchId || '', qcType: r.qcTypeName || '', parameter: r.analyte || '',
    numericResult: r.value != null && isFinite(r.value) ? fmtNum(r.value) : '', result: r.result || '',
    units: r.units || '', analyzedDate: r.analyzedDate || '',
  });
  const rawSpike = rows.filter((r) => r.kind === 'S').map(toRaw);
  const rawBlank = rows.filter((r) => r.kind === 'B').map(toRaw);

  const typeLabel = meta.modelType === 'verif' ? 'MDL Verification' : 'Initial MDL';
  const range = (meta.dateFrom || '') + ' → ' + (meta.dateTo || '');

  // Compliance checks vs 40 CFR 136 App. B (gaps 3.1 / 3.2 / 3.3) — computed from the
  // retrieved rows; surfaced to the analyst (do not change the MDL value).
  const batches = new Set(rows.map((r) => (r.qcBatchId || '').trim()).filter(Boolean));
  const dates = new Set(rows.map((r) => (r.analyzedDate || '').slice(0, 10)).filter(Boolean));   // MM/DD/YYYY portion
  const spikeLevel = analytes.map((a) => {
    const ratio = (a.current != null && a.current > 0 && isFinite(a.spike)) ? a.spike / a.current : null;
    const flag: 'ok' | 'low' | 'high' | 'na' = ratio == null ? 'na' : ratio < 2 ? 'low' : ratio > 10 ? 'high' : 'ok';
    return { analyte: a.name, ratio, flag };
  });
  // Spike cadence — ≥2 MDL spikes per calendar quarter across the verification window (§3 / gap 4.2).
  const isVerif = meta.modelType === 'verif';
  const qLabel = (idx: number) => `${Math.floor(idx / 4)} Q${(idx % 4) + 1}`;          // quarter index → "YYYY Qn"
  const qIdxOfIso = (iso: string) => (+iso.slice(0, 4)) * 4 + Math.floor(((+iso.slice(5, 7)) - 1) / 3);
  const qIdxOfUs = (s: string) => { const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s || ''); return m ? (+m[3]!) * 4 + Math.floor(((+m[1]!) - 1) / 3) : null; };
  const cadence: { quarter: string; spikes: number; ok: boolean }[] = [];
  if (isVerif && meta.dateFrom && meta.dateTo) {
    const spikeQ = new Map<string, number | null>();                                    // distinct spike sample → quarter index
    rows.filter((r) => r.kind === 'S').forEach((r) => {
      const k = `${r.qcTypeName ?? ''}|${baseSysCode(r.sysSampleCode)}|${r.qcBatchId ?? ''}`;
      if (!spikeQ.has(k)) spikeQ.set(k, qIdxOfUs(r.analyzedDate || ''));
    });
    const counts: Record<number, number> = {};
    for (const qi of spikeQ.values()) if (qi != null) counts[qi] = (counts[qi] || 0) + 1;
    const lo = qIdxOfIso(meta.dateFrom), hi = qIdxOfIso(meta.dateTo);
    // C3 (feedback 2026-06-26): a quarter with no MDL spikes means no analyses ran that
    // quarter, so the ≥2/quarter cadence does not apply — only quarters that HAD spikes
    // but fewer than 2 are flagged.
    for (let i = lo; i <= hi; i++) { const n = counts[i] || 0; cadence.push({ quarter: qLabel(i), spikes: n, ok: n === 0 || n >= 2 }); }
  }
  const compliance: MdlCompliance = {
    batches: batches.size, dates: dates.size, spreadOk: batches.size >= 3 && dates.size >= 3,
    nSpike: nS, nBlank: nB, minSpikeOk: nS >= 7, minBlankOk: nB >= 7,
    // C1 (feedback 2026-06-26): the spike level is checked against the ESTABLISHED MDL,
    // which is itself an estimate — so an out-of-band spike is shown for information only
    // (the spikeLevel[] flags drive the display) and is never a save-blocking failure.
    spikeLevel, spikeLevelOk: true,
    cadence, cadenceOk: cadence.every((c) => c.ok),
  };

  return {
    mdlId: '', title: '', sub: '', modelType: meta.modelType, status: 'draft',
    multiAnalyte: true, analytes, _rawSpike: rawSpike, _rawBlank: rawBlank,
    _spikeAmtByType: spikeAmtByTypeArr,
    _spikeLevels: spikeLevels,
    _compliance: compliance,
    nSpike: nS, nBlank: nB, tValue: stats[0]?.t,
    kpis: [
      { lbl: 'Analytes', val: String(order.length), unit: '', cls: 'blue', sub: meta.test || '' },
      { lbl: 'Spiked (LFB) samples', val: String(nS), unit: '', cls: '', color: '#185FA5', sub: 't × S<sub>s</sub>' },
      { lbl: 'Blank (LRB) samples', val: String(nB), unit: '', cls: '', color: '#A32D2D', sub: 'X̄ + t × S<sub>b</sub>' },
      { lbl: typeLabel, val: '40 CFR 136 B', unit: '', cls: 'green', sub: range },
    ],
    calcRows: [], docRows: [],
    _matrixBuilt: true, _spikeRows: spikeRows, _blankRows: blankRows, _stats: stats,
  };
}

/* ════════ Raw Results (flat per-measurement listing) — feedback2 #4 ════════ */
export interface RawResultRow {
  sysSampleCode: string; qcBatchId: string; qcType: string; parameter: string;
  numericResult: string; result: string; units: string; analyzedDate: string;
}
function rawResultsTable(rows: RawResultRow[]): string {
  let h = '<table class="xaf-table" style="table-layout:auto"><thead><tr>'
    + '<th>QCBatchID</th><th>SysSampleCode</th><th>QCType</th><th>Parameter</th><th class="num">NumericResult</th>'
    + '<th>Result</th><th>Units</th><th>AnalyzedDate</th></tr></thead><tbody>';
  if (rows.length === 0) h += '<tr><td colspan="8" style="color:#999;padding:10px">No rows.</td></tr>';
  rows.forEach((r, i) => {
    h += '<tr class="' + (i % 2 ? 'zebra' : '') + '">'
      + '<td>' + esc(r.qcBatchId || '—') + '</td><td>' + esc(r.sysSampleCode || '—') + '</td>'
      + '<td>' + esc(r.qcType || '—') + '</td>'
      + '<td>' + esc(r.parameter || '—') + '</td><td class="num">' + esc(r.numericResult || '—') + '</td>'
      + '<td>' + esc(r.result || '—') + '</td><td>' + esc(r.units || '—') + '</td><td>' + esc(r.analyzedDate || '—') + '</td></tr>';
  });
  return h + '</tbody></table>';
}
/** Raw Results tab — Spike Samples + Method Blanks sections, identical columns. */
export function renderRawResultsView(spike: RawResultRow[], blank: RawResultRow[]): string {
  return matrixSection('Spike Samples', spike.length + ' rows', rawResultsTable(spike))
    + matrixSection('Method Blanks', blank.length + ' rows', rawResultsTable(blank));
}

/** Per-analyte create payload for the multi-analyte Save endpoint — transposes
 *  the flat retrieve rows into one entry per analyte (its spiked/blank
 *  replicate measurements).  The server recomputes each analyte's MDL. */
export interface RetrieveAnalytePayload {
  parameter: string; units: string | null; spikeLevel: number; referenceMdl: number | null;
  spikes: { batch: string; sample: string; sysSampleCode: string; resultText: string; date: string; spikeConc: number; result: number }[];
  blanks: { batch: string; sample: string; sysSampleCode: string; resultText: string; date: string; result: number; numerical: boolean; note: string | null }[];
}
export function buildRetrieveAnalytePayload(rows: RetrieveFlatRow[]): RetrieveAnalytePayload[] {
  const order: string[] = []; const seen = new Set<string>();
  for (const r of rows) { if (r.analyte && !seen.has(r.analyte)) { seen.add(r.analyte); order.push(r.analyte); } }
  return order.map((nm) => {
    const sRows = rows.filter((r) => r.analyte === nm && r.kind === 'S');
    const bRows = rows.filter((r) => r.analyte === nm && r.kind === 'B');
    const amts = sRows.map((r) => r.spikeAmount).filter((v): v is number => v != null && isFinite(v));
    const spikeLevel = amts.length ? amts.reduce((a, b) => a + b, 0) / amts.length : 0;
    const units = (sRows[0]?.units ?? bRows[0]?.units) ?? null;
    const cur = [...sRows, ...bRows].map((r) => r.currentMdl).find((v): v is number => v != null && isFinite(v)) ?? null;
    return {
      parameter: nm, units, spikeLevel, referenceMdl: cur,
      /* The stored replicate identifies its SAMPLE, not its QC position: SysSampleCode
         takes the LOGGED-IN code ('QB26051203_032-LFB1') and falls back to the SDMS
         position label only when there is no logged-in code. Saving the position label
         ('LCS Digested1') made the saved study — and Step 5's matrix — repeat one QC
         type across every replicate instead of naming the samples. The two replicates
         of a run stay distinguishable because the logged-in code is numbered (-LFB1 /
         -LFB2). SampleId keeps SDMS's own SampleID where it has one. */
      spikes: sRows.filter((r) => r.value != null && isFinite(r.value)).map((r) => ({
        batch: r.qcBatchId || '—', sample: r.sampleId || r.loginSampleCode || '—',
        sysSampleCode: r.loginSampleCode || r.sysSampleCode || '', resultText: r.result || '', date: r.analyzedDate ?? '',
        spikeConc: (r.spikeAmount != null && isFinite(r.spikeAmount)) ? r.spikeAmount : spikeLevel, result: r.value as number,
      })),
      blanks: bRows.filter((r) => r.value != null && isFinite(r.value)).map((r) => ({
        batch: r.qcBatchId || '—', sample: r.sampleId || r.loginSampleCode || '—',
        sysSampleCode: r.loginSampleCode || r.sysSampleCode || '', resultText: r.result || '', date: r.analyzedDate ?? '', result: r.value as number, numerical: true, note: null,
      })),
    };
  });
}

/* ════════ PDF report (print-ready window) ════════ */
function verifLabelFor(s: string): string { return ({ pass: 'Pass', fail: 'Update', flag: 'Low', update: 'Update', low: 'Low' } as Record<string, string>)[s] || '—'; }
function verifColorFor(s: string): string { return (s === 'pass') ? '#0F6E56' : (s === 'fail' || s === 'update') ? '#A32D2D' : '#854F0B'; }
export function statusTitle(s: StudyStatus): string { return ({ pending: 'PENDING SUBMISSION', draft: 'SUBMITTED', validated: 'VALIDATED', approved: 'APPROVED', released: 'RELEASED' } as Record<string, string>)[s] || 'PENDING SUBMISSION'; }
export function watermarkLabel(s: StudyStatus): string { return ({ pending: 'PENDING SUBMISSION', draft: 'SUBMITTED', validated: 'FOR REVIEW', approved: 'PENDING APPROVAL' } as Record<string, string>)[s] || 'PENDING SUBMISSION'; }

/** Print-adapted CSS for the on-screen MDL Study matrix (the "grid layout" report). */
const MTX_REPORT_CSS =
  '.mtx-wrap{overflow:visible;border:1px solid #ccc;border-radius:4px;margin-bottom:10px}' +
  '.mtx{border-collapse:collapse;width:100%;font-size:9.5px}' +
  '.mtx th,.mtx td{border:1px solid #ddd;padding:3px 6px;text-align:center;white-space:nowrap}' +
  '.mtx thead th{background:#185FA5;color:#fff;font-weight:bold;vertical-align:bottom}' +
  '.mtx .an-unit{display:block;font-weight:normal;font-style:italic;color:#cfe0f0;font-size:8.5px}' +
  '.mtx td.num{text-align:right;font-variant-numeric:tabular-nums;font-feature-settings:"tnum"}' +
  '.mtx .col-batch{text-align:left;font-variant-numeric:tabular-nums}' +
  '.mtx tbody tr.zebra td{background:#f6f9fc}' +
  '.mtx tr.mtx-spikeamt td{background:#fff8ec;font-weight:bold;color:#854F0B}' +
  '.mtx tr.mtx-stat td{background:#f3f3f3;font-weight:bold}' +
  '.mtx tr.mtx-stat .col-batch{text-transform:uppercase;font-size:8.5px;color:#444}' +
  '.mtx tr.mtx-final td{background:#fff1e6}.mtx tr.mtx-final td.num{color:#c45a08;font-weight:bold}' +
  '.mtx tr.mtx-grouphdr td{background:#eef3f8;color:#185FA5;font-weight:bold;text-align:left}' +
  '@media print{@page{size:landscape;margin:12mm}}';

export function buildReportHtml(d: Study, status: StudyStatus, layout: 'standard' | 'grid' = 'standard'): string {
  const isReleased = status === 'released';
  const wmLabel = watermarkLabel(status);
  const watermark = isReleased ? '' : '<div class="watermark"><span>' + wmLabel + '</span></div>';
  const banner = isReleased
    ? ''
    : '<div class="banner unctrl">UNCONTROLLED COPY · ' + wmLabel + ' · NOT FOR DISTRIBUTION</div>';
  const isVerif = d.modelType === 'verif';
  const sigBlocks = (d._signatures || []).map((s) => {
    const std = (s.mode === 'part11' ? '21 CFR Part 11' : 'ISO 17025') + (s.mfa ? ' +MFA' : '');
    return '<div class="sigblock">' +
      '<div class="sig-mark">/s/ ' + s.name + '</div>' +
      '<div class="sig-name">' + s.name + ' · ' + (s.role || '—') + ' (' + s.userId + ')</div>' +
      '<div class="sig-meta"><b>' + s.action + '</b> — ' + s.meaning + '</div>' +
      '<div class="sig-meta">' + fmtSigDate(s.when) + ' · ' + std + (s.reason ? ' · ' + s.reason : '') + '</div>' +
      '<div class="sig-hash">SHA-256: ' + (s.recordHash || '—') + '</div>' +
      '</div>';
  }).join('');

  const metaRows = (d.docRows || []).map((r) => '<tr><td class="k">' + r[0] + '</td><td>' + r[1] + '</td></tr>').join('');
  const calcRows = (d.calcRows || []).map((r) => {
    const hl = r[3] ? ' class="hl"' : '';
    return '<tr' + hl + '><td class="k">' + r[0] + '</td><td>' + r[1] + '</td><td class="note">' + (r[2] || '') + '</td></tr>';
  }).join('');

  let resultsBlock = '';
  if (d.multiAnalyte && d.analytes && layout === 'grid') {
    // Grid-layout report — reproduce the on-screen MDL Study matrix (Spiked Samples,
    // Method Blanks, Statistics & MDL Summary), the same layout as in the grid.
    if (!d._matrixBuilt) buildMatrix(d);
    resultsBlock = '<h3>MDL Study Matrix (' + d.analytes.length + ' analytes)</h3>' + renderMatrixView(d);
  } else if (d.multiAnalyte && d.analytes) {
    if (!d._matrixBuilt) buildMatrix(d);
    const head = '<tr><th>Analyte</th><th>Unit</th><th>Spike</th><th>MDL Spike</th><th>MDL Blank</th><th>Final MDL</th>' +
      (isVerif ? '<th>Current MDL</th><th>Verification</th>' : '') + '</tr>';
    const body = d.analytes.map((a, ai) => {
      const s = (d._stats && d._stats[ai]) || { mdlSpk: a.mdls, mdlBlk: a.mdlb, final: a.final, current: a.current ?? null, verif: a.status } as Partial<MatrixStat>;
      let row = '<tr><td class="an">' + a.name + '</td><td>' + a.unit + '</td><td class="num">' + fmtNum(a.spike) +
        '</td><td class="num">' + fmtNum(s.mdlSpk as number) + '</td><td class="num">' + fmtNum(s.mdlBlk as number) +
        '</td><td class="num" style="font-weight:bold">' + fmtNum(s.final as number) + '</td>';
      if (isVerif) {
        const st = (s.verif as string) || a.status || '';
        row += '<td class="num">' + (s.current != null ? fmtNum(s.current) : '—') + '</td>' +
          '<td style="color:' + verifColorFor(st) + ';font-weight:bold">' + verifLabelFor(st) + '</td>';
      }
      return row + '</tr>';
    }).join('');
    resultsBlock = '<h3>Per-Analyte MDL Results (' + d.analytes.length + ' analytes)</h3>' +
      '<table class="rep-tbl"><thead>' + head + '</thead><tbody>' + body + '</tbody></table>';
  } else {
    resultsBlock = '<h3>Spiked Samples</h3><div class="foot">' + (d.spikeFooter || '') + '</div>' +
      '<h3>Method Blanks</h3><div class="foot">' + (d.blankFooter || '') + '</div>';
  }

  const kpiBlock = (d.kpis || []).map((k) => '<div class="kpi"><div class="kl">' + k.lbl + '</div><div class="kv">' + k.val +
    (k.unit ? ' <span class="ku">' + k.unit + '</span>' : '') + '</div><div class="ks">' + (k.sub || '') + '</div></div>').join('');

  /* Masthead document band (Variant A): MDL number + bold-test description + status pill.
     PARKED, NOT DELETED (2026-08-26). `docSub` was computed but never consumed by the
     markup below — the `.docband` style at the foot of this file is in place, but the
     subtitle was never wired into it. That unused local is a HARD `tsc` error (TS6133)
     under this project's strict config, so it broke `npm run build` outright, which is
     how the web bundle came to be built with `npx vite build` instead — silently
     skipping the `prebuild` patch guard and shipping a bundle WITHOUT the Univer
     patches. Left commented rather than removed so the QA/QC owner keeps the exact
     snippet; wiring it into the docband would change the report's output, which is
     that owner's call, not a build fix. Re-enable by using `docSub` in the markup. */
  // const titleClean = d.title.replace(d.mdlId + ' — ', '');
  // const tSeg = titleClean.split(' · ');
  // const docSub = '<b>' + tSeg[0] + '</b>' + (tSeg.length > 1 ? ' · ' + tSeg.slice(1).join(' · ') : '') + '<br>' + d.sub;

  return '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' + d.mdlId + ' — MDL Report</title><style>' +
    '*{box-sizing:border-box}body{font-family:"Segoe UI",Tahoma,system-ui,-apple-system,Roboto,"Helvetica Neue",Arial,sans-serif;color:#14161b;margin:0;padding:28px 34px;font-size:12px}' +
    'h1{font-size:19px;margin:0 0 2px}h2{font-size:13px;color:#666;font-weight:normal;margin:0 0 14px}' +
    'h3{font-size:13px;margin:18px 0 6px;color:#185FA5;border-bottom:1px solid #ddd;padding-bottom:3px}' +
    '.mast{display:flex;align-items:flex-start;gap:28px}' +
    '.brand{display:flex;flex-direction:column;gap:7px}.brand img{height:58px;width:58px;object-fit:contain;display:block;border:0}.brand .labname{font-size:12.5px;font-weight:bold;color:#15315B;line-height:1.25;max-width:320px}.brand .addr{font-size:11.5px;color:#6b7683;letter-spacing:.02em}' +
    '.titlelock{flex:1;text-align:right}.titlelock .eyebrow{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;color:#185FA5;font-weight:bold;margin-bottom:5px}' +
    '.titlelock h1{margin:0;font-size:25px;font-weight:bold;color:#15315B;letter-spacing:-.01em;line-height:1.05}.titlelock .date{font-size:11.5px;color:#6b7683;margin-top:6px}' +
    '.rule{height:2px;background:#15315B;margin:16px 0 0;position:relative;border-radius:2px}.rule::before{content:"";position:absolute;left:0;top:0;height:2px;width:52px;background:#c45a08;border-radius:2px}' +
    '.docband{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding:16px 0 4px;margin-bottom:8px}' +
    '.docid .num{font-family:ui-monospace,"Cascadia Mono",Consolas,monospace;font-size:23px;font-weight:bold;color:#1f2733;letter-spacing:.01em;line-height:1}.docid .subx{margin-top:8px;font-size:12px;color:#6b7683;line-height:1.55}.docid .subx b{color:#495260;font-weight:bold}' +
    '.docmeta{display:flex;flex-direction:column;align-items:flex-end;gap:8px;flex-shrink:0}' +
    '.pill{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:bold;letter-spacing:.06em;text-transform:uppercase;padding:5px 11px;border-radius:20px}' +
    '.pill.released{background:#e7f3ec;color:#1f7a4d}.pill.released::before{content:"";width:7px;height:7px;border-radius:50%;background:#1f7a4d}' +
    '.pill.draft{background:#fef3e2;color:#b45309}.pill.draft::before{content:"";width:7px;height:7px;border-radius:50%;background:#b45309}' +
    '.chip{font-size:11.5px;color:#185FA5;background:#eaf1f8;border:1px solid #d4e2f0;padding:4px 10px;border-radius:6px;font-weight:bold}' +
    'table{border-collapse:collapse;width:100%;margin-bottom:6px}td,th{border:1px solid #ccc;padding:4px 7px;text-align:left;font-size:11px;vertical-align:top}' +
    'th{background:#eee}.k{background:#f6f6f6;font-weight:bold;width:34%}.note{color:#777;font-size:10px}.hl td{background:#fff1e6}.hl .k{background:#ffe0c7;color:#c45a08}' +
    '.rep-tbl th{background:#185FA5;color:#fff}.rep-tbl .an{font-weight:bold}.num{text-align:right;font-variant-numeric:tabular-nums;font-feature-settings:"tnum"}' +
    '.kpis{display:flex;gap:10px;margin:6px 0 4px}.kpi{flex:1;border:1px solid #ddd;border-radius:4px;padding:7px 9px}' +
    '.kl{font-size:9px;text-transform:uppercase;color:#888}.kv{font-size:17px;font-weight:bold;color:#185FA5}.ku{font-size:11px}.ks{font-size:9px;color:#999}' +
    '.foot{font-size:11px;color:#444;padding:4px 0 8px}.sigs th{background:#534AB7;color:#fff}' +
    '.sigwrap{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:4px}' +
    '.sigblock{border:1px solid #ccc;border-radius:4px;padding:8px 10px;break-inside:avoid}' +
    '.sig-mark{font-family:\'Segoe Script\',\'Brush Script MT\',\'Lucida Handwriting\',cursive;font-size:21px;color:#15315b;border-bottom:1px solid #bbb;padding-bottom:3px;margin-bottom:5px}' +
    '.sig-name{font-size:11px;font-weight:bold;color:#222}.sig-meta{font-size:10px;color:#555;margin-top:2px}' +
    '.sig-hash{font-family:"Courier New",monospace;font-size:8.5px;color:#0F6E56;margin-top:5px;word-break:break-all}' +
    '.watermark{position:fixed;inset:0;z-index:1000;pointer-events:none;display:flex;align-items:center;justify-content:center}' +
    '.watermark span{font-size:120px;color:rgba(190,30,30,.13);font-weight:bold;transform:rotate(-30deg);letter-spacing:10px;border:8px solid rgba(190,30,30,.13);padding:14px 50px;border-radius:12px;white-space:nowrap}' +
    '.banner{text-align:center;font-weight:bold;font-size:12px;padding:6px;margin-bottom:12px;border-radius:3px;letter-spacing:1px}' +
    '.banner.unctrl{background:#FAECE7;color:#A32D2D;border:1px solid #e3b4a8}' +
    '.banner.ctrl{background:#E1F5EE;color:#0F6E56;border:1px solid #aedcc9}' +
    '@media print{.watermark span{-webkit-print-color-adjust:exact;print-color-adjust:exact}}' +
    '.fin{margin-top:18px;border-top:1px solid #ccc;padding-top:8px;font-size:10px;color:#888;display:flex;justify-content:space-between}' +
    '@media print{body{padding:0}@page{margin:14mm}}' +
    (layout === 'grid' ? MTX_REPORT_CSS : '') +
    '</style></head><body>' +
    watermark + banner +
    '<div class="mast">' +
      '<div class="brand"><img src="' + HOUSTON_LOGO_DATA_URI + '" alt="' + LAB_ORG + '" />' +
        '<div class="labname">' + LAB_NAME + '</div>' +
        '<div class="addr">' + LAB_ADDRESS.join(' · ') + '</div></div>' +
      '<div class="titlelock">' +
        '<div class="eyebrow">Method Detection Limit Study</div>' +
        '<h1>' + (isVerif ? 'MDL Verification Study Report' : 'Initial MDL Study Report') + '</h1>' +
        '<div class="date">Report Date · ' + fmtReportDate(new Date()) + '</div>' +
      '</div>' +
    '</div>' +
    '<div class="rule"></div>' +
    '<div class="kpis">' + kpiBlock + '</div>' +
    '<h3>Study Information</h3><table>' + metaRows + '</table>' +
    '<h3>Calculation Summary</h3><table>' + calcRows + '</table>' +
    resultsBlock +
    '<h3>Electronic Signatures &amp; Audit Trail</h3>' +
    '<div class="sigwrap">' + (sigBlocks || '<div style="color:#999">No signatures on file.</div>') + '</div>' +
    '<div style="font-size:9px;color:#999;margin-top:6px">Each signature is the legally binding equivalent of a handwritten signature and is bound to this record by the SHA-256 hash shown (tamper-evident) per 21 CFR Part 11 §11.70 — none can be altered or removed.</div>' +
    '<div class="fin"><span>' + LAB_NAME + ' · Method Detection Limit Study</span><span>' + d.mdlId + ' · ' + status.toUpperCase() + '</span></div>' +
    '</body></html>';
}

/* ════════ role authority (e-sign workflow gate) ════════ */
export const ROLE_AUTHORITY: Record<string, string[]> = {
  Submit: ['Analyst', 'Reviewer', 'QA Manager', 'Lab Director'],
  Validate: ['Reviewer', 'QA Manager', 'Lab Director'],
  Approve: ['QA Manager', 'Lab Director'],
  Release: ['QA Manager', 'Lab Director'],
};
export const ACTION_REQ: Record<string, string> = {
  Submit: 'Analyst or above', Validate: 'Reviewer or above', Approve: 'QA Manager / Lab Director', Release: 'QA Manager / Lab Director',
};
